import React, { useMemo, useState, useEffect } from "react";
import { Table, Badge, Collapse, Button, Spinner } from "reactstrap";
import { resolveSiteWorkTypeDisplay } from "../../../../utils/otherValueDisplay";
import { formatNumberValue } from "../utils/assetValuationUtils";
import { getSiteWorkPhotosBySiteWork, deleteSiteWork } from "../../../../services/ValitanApiService";
import { resolveBackendUrl } from "../../../../helpers/serverUrl";
import Swal from "sweetalert2";

const normalizeMediaUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
        return url;
    }
    return resolveBackendUrl(url);
};

const extractPhotoList = (response) => {
    const payload = response?.data?.data ?? response?.data ?? response ?? [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const normalizePhotoUrls = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
};

// Add custom styles for reference number grouping
// Colors alternate by reference_number group to visually separate different reference numbers
// All site works with the same reference_number (even if serial numbers are 1, 2, 3) will have the same color
const customStyles = `
    .siteworks-table tbody tr.ref-group-0,
    .siteworks-table.table tbody tr.ref-group-0,
    .siteworks-table.table-sm tbody tr.ref-group-0,
    table.siteworks-table tbody tr.ref-group-0,
    table.siteworks-table.table tbody tr.ref-group-0,
    table.siteworks-table.table-sm tbody tr.ref-group-0 {
        background-color: #e8f0f5 !important;
    }
    .siteworks-table tbody tr.ref-group-0 td,
    .siteworks-table.table tbody tr.ref-group-0 td,
    .siteworks-table.table-sm tbody tr.ref-group-0 td,
    table.siteworks-table tbody tr.ref-group-0 td,
    table.siteworks-table.table tbody tr.ref-group-0 td,
    table.siteworks-table.table-sm tbody tr.ref-group-0 td {
        background-color: #e8f0f5 !important;
    }
    .siteworks-table tbody tr.ref-group-0:hover,
    .siteworks-table.table tbody tr.ref-group-0:hover,
    .siteworks-table.table-sm tbody tr.ref-group-0:hover,
    table.siteworks-table tbody tr.ref-group-0:hover,
    table.siteworks-table.table tbody tr.ref-group-0:hover,
    table.siteworks-table.table-sm tbody tr.ref-group-0:hover {
        background-color: #d8e5ed !important;
    }
    .siteworks-table tbody tr.ref-group-0:hover td,
    .siteworks-table.table tbody tr.ref-group-0:hover td,
    .siteworks-table.table-sm tbody tr.ref-group-0:hover td,
    table.siteworks-table tbody tr.ref-group-0:hover td,
    table.siteworks-table.table tbody tr.ref-group-0:hover td,
    table.siteworks-table.table-sm tbody tr.ref-group-0:hover td {
        background-color: #d8e5ed !important;
    }
    .siteworks-table tbody tr.ref-group-1,
    .siteworks-table.table tbody tr.ref-group-1,
    .siteworks-table.table-sm tbody tr.ref-group-1,
    table.siteworks-table tbody tr.ref-group-1,
    table.siteworks-table.table tbody tr.ref-group-1,
    table.siteworks-table.table-sm tbody tr.ref-group-1 {
        background-color: #ffffff !important;
    }
    .siteworks-table tbody tr.ref-group-1 td,
    .siteworks-table.table tbody tr.ref-group-1 td,
    .siteworks-table.table-sm tbody tr.ref-group-1 td,
    table.siteworks-table tbody tr.ref-group-1 td,
    table.siteworks-table.table tbody tr.ref-group-1 td,
    table.siteworks-table.table-sm tbody tr.ref-group-1 td {
        background-color: #ffffff !important;
    }
    .siteworks-table tbody tr.ref-group-1:hover,
    .siteworks-table.table tbody tr.ref-group-1:hover,
    .siteworks-table.table-sm tbody tr.ref-group-1:hover,
    table.siteworks-table tbody tr.ref-group-1:hover,
    table.siteworks-table.table tbody tr.ref-group-1:hover,
    table.siteworks-table.table-sm tbody tr.ref-group-1:hover {
        background-color: #f5f5f5 !important;
    }
    .siteworks-table tbody tr.ref-group-1:hover td,
    .siteworks-table.table tbody tr.ref-group-1:hover td,
    .siteworks-table.table-sm tbody tr.ref-group-1:hover td,
    table.siteworks-table tbody tr.ref-group-1:hover td,
    table.siteworks-table.table tbody tr.ref-group-1:hover td,
    table.siteworks-table.table-sm tbody tr.ref-group-1:hover td {
        background-color: #f5f5f5 !important;
    }
`;

const SiteWorksTab = ({ assets = [], onRefresh }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [siteWorkPhotos, setSiteWorkPhotos] = useState({});
    const [loadingAllPhotos, setLoadingAllPhotos] = useState(false);

    const reviewRows = useMemo(
        () =>
            assets.map((item) => ({
                ...item,
                _siteWorkId: String(item?.id || item?.asset_id || item?.site_work_id || item?.siteWorkId || ""),
                _photoUrls: normalizePhotoUrls(item?.photo_urls || item?.photoUrls).map((url) => normalizeMediaUrl(url)),
            })),
        [assets]
    );

    useEffect(() => {
        if (reviewRows.length === 0) {
            setSiteWorkPhotos({});
            return;
        }

        let isMounted = true;

        const fetchSiteWorkPhotos = async () => {
            setLoadingAllPhotos(true);
            try {
                const uniqueSiteWorkIds = [...new Set(
                    reviewRows
                        .map((asset) => asset._siteWorkId)
                        .filter(Boolean)
                        .map(String)
                )];

                const photoEntries = await Promise.all(
                    uniqueSiteWorkIds.map(async (siteWorkId) => {
                        try {
                            const response = await getSiteWorkPhotosBySiteWork(siteWorkId);
                            return [siteWorkId, extractPhotoList(response).map((photo) => ({
                                ...photo,
                                photo_url: normalizeMediaUrl(photo?.photo_url || photo?.photoUrl || photo?.url || photo?.path || ""),
                            }))];
                        } catch (error) {
                            console.error(`Error fetching photos for sitework ${siteWorkId}:`, error);
                            return [siteWorkId, []];
                        }
                    })
                );

                if (!isMounted) return;

                const nextPhotos = Object.fromEntries(photoEntries);
                reviewRows.forEach((row) => {
                    if (row._siteWorkId && (!nextPhotos[row._siteWorkId] || nextPhotos[row._siteWorkId].length === 0) && row._photoUrls.length > 0) {
                        nextPhotos[row._siteWorkId] = row._photoUrls.map((url, idx) => ({
                            id: `${row._siteWorkId}-fallback-${idx}`,
                            photo_url: url,
                            photo_type: "Photo",
                        }));
                    }
                });
                setSiteWorkPhotos(nextPhotos);
            } catch (error) {
                console.error("Error fetching sitework photos:", error);
                setSiteWorkPhotos({});
            } finally {
                if (isMounted) {
                    setLoadingAllPhotos(false);
                }
            }
        };

        fetchSiteWorkPhotos();

        return () => {
            isMounted = false;
        };
    }, [reviewRows]);

    const toggleRow = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    const handleDelete = async (asset) => {
        const siteWorkId = asset._siteWorkId;
        if (!siteWorkId) return;

        const result = await Swal.fire({
            title: "Delete Site Work?",
            text: "Are you sure you want to remove this site work? This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                await deleteSiteWork(siteWorkId);
                Swal.fire("Deleted!", "Site work has been removed.", "success");
                if (onRefresh) onRefresh();
            } catch (error) {
                console.error("Error deleting site work:", error);
                Swal.fire("Error", "Could not delete site work. It might have dependencies.", "error");
            }
        }
    };

    if (reviewRows.length === 0) {
        return <p className="text-center text-muted py-4">No site works recorded.</p>;
    }

    // Calculate unique reference numbers once for color assignment
    const refNumbers = [...new Set(reviewRows.map(asset => asset.reference_number || asset.valuation_ref || "").filter(Boolean))];
    
    // Color palette for alternating groups (light colors for better readability)
    // All site works with the same reference_number will get the same color
    // Even if they have different site_work_serial_no (1, 2, 3), they share the same reference_number color
    const groupColors = [
        "#e8f0f5", // Light blue-gray - for first reference_number group (more visible)
        "#ffffff", // White - for second reference_number group
    ];

    return (
        <>
            <style>{customStyles}</style>
            <Table responsive bordered className="table-sm siteworks-table">
            <thead style={{ backgroundColor: "#0147C8", color: "white" }}>
                <tr>
                    <th style={{ width: "70px" }}>#</th>
                    <th>Site Work Serial No</th>
                    <th>Reference Number</th>
                    <th>Site Work Type</th>
                    <th>Description</th>
                    <th>Dimensions</th>
                    <th>Measurement (sqm)</th>
                    <th>Construction Rate</th>
                    <th>Location</th>
                    <th>Ward</th>
                    <th>Institution</th>
                    <th>Valuer</th>
                    <th>Status</th>
                    <th>Condition</th>
                    <th>Remark</th>
                </tr>
            </thead>
            <tbody>
                {reviewRows.map((a, index) => {
                    const siteWorkId = a._siteWorkId;
                    const isExpanded = expandedRows.has(siteWorkId);
                    const condition = a.observed_condition_name || a.observed_condition;
                    const hasCondition = condition && condition.trim() !== "" && condition !== "N/A";
                    
                    // Get current reference number
                    const currentRef = a.reference_number || a.valuation_ref || "";
                    // Get color index for this reference number group
                    // All site works with the same reference_number will have the same color
                    // Even if site_work_serial_no is 1, 2, 3, they all share the same reference_number color
                    const refIndex = refNumbers.indexOf(currentRef);
                    const colorIndex = refIndex >= 0 ? (refIndex % groupColors.length) : 1; // Default to white if not found
                    const rowBgColor = currentRef ? groupColors[colorIndex] : "#ffffff";
                    
                    // Add custom CSS class for persistent background
                    // This ensures all rows with the same reference_number have the same color
                    // Rows 1, 2, 3 with same reference_number will all have ref-group-0 or ref-group-1
                    const rowClassName = currentRef ? `ref-group-${colorIndex}` : "";
                    
                    // Style for the row: background color only
                    // Using both inline style and CSS class to ensure colors are visible
                    const rowStyle = {
                        backgroundColor: rowBgColor
                    };
                    
                    return (
                        <React.Fragment key={siteWorkId || index}>
                            <tr className={rowClassName} style={rowStyle}>
                                <td className="fw-bold text-muted" style={{ backgroundColor: rowBgColor }}>
                                    <div className="d-flex align-items-center gap-1">
                                        <span>{index + 1}</span>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => toggleRow(siteWorkId)}
                                            className="p-0"
                                            style={{ color: "#0147C8", minWidth: "20px" }}
                                        >
                                            <i className={`mdi ${isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                                        </Button>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => handleDelete(a)}
                                            className="p-0 text-danger ms-1"
                                            style={{ minWidth: "20px" }}
                                            title="Remove Site Work"
                                        >
                                            <i className="mdi mdi-delete-outline" />
                                        </Button>
                                    </div>
                                </td>
                                <td>{a.site_work_serial_no || a.siteWorkSerialNo || "-"}</td>
                                <td>{a.reference_number || a.valuation_ref || "-"}</td>
                                <td><strong>{resolveSiteWorkTypeDisplay(a)}</strong></td>
                                <td>{a.description || "-"}</td>
                                <td>{a.dimensions || "-"}</td>
                                <td>{a.measurement || "-"}</td>
                                <td>{a.construction_rate || a.constructionRate ? formatNumberValue(a.construction_rate ?? a.constructionRate) : "-"}</td>
                                <td>{a.location || a.ward_name || "-"}</td>
                                <td>{a.ward_name || "-"}</td>
                                <td>{a.institution_name || "-"}</td>
                                <td>
                                    {(() => {
                                        const valuation = a.asset_valuation || a.assetValuation || a.valuation;
                                        const valuer = valuation?.valuer_user || valuation?.valuerUser;
                                        const valuerName =
                                            a.valuer_name || a.valuerName ||
                                            (valuer
                                                ? [
                                                      valuer.first_name || valuer.firstName || "",
                                                      valuer.middle_name || valuer.middleName || "",
                                                      valuer.last_name || valuer.lastName || "",
                                                  ]
                                                      .filter(Boolean)
                                                      .join(" ")
                                                : "-");
                                        return valuerName;
                                    })()}
                                </td>
                                <td>{a.status || a.valuation_status || "-"}</td>
                                <td>{hasCondition ? condition : "-"}</td>
                                <td>{a.remark || "-"}</td>
                            </tr>
                            {isExpanded && (
                                <tr className={rowClassName} style={rowStyle}>
                                    <td colSpan="15" className="p-0">
                                        <Collapse isOpen={isExpanded}>
                                            <div className="p-3 bg-light">
                                                <div className="row">
                                                    {/* Site Work Information */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Site Work Details</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Site Work Type:</td>
                                                                    <td>{resolveSiteWorkTypeDisplay(a)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Other Specify:</td>
                                                                    <td>{a.other_specify || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Description:</td>
                            <td>{a.description || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Dimensions:</td>
                                                                    <td>{a.dimensions || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Measurement (sqm):</td>
                                                                    <td>{a.measurement || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Construction Rate:</td>
                                                                    <td>{a.construction_rate || a.constructionRate ? formatNumberValue(a.construction_rate ?? a.constructionRate) : "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Serial Number:</td>
                                                                    <td>{a.serial_number || a.serialNumber || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Observed Condition:</td>
                                                                    <td>{condition || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Remark:</td>
                                                                    <td>{a.remark || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Asset Basic Information */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Asset Information</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Asset Name:</td>
                                                                    <td>{a.name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Category:</td>
                                                                    <td>{a.category_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Quantity:</td>
                                                                    <td>{a.quantity || 1}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Barcode:</td>
                                                                    <td>{a.barcode || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Building Number:</td>
                                                                    <td>{a.building_number || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Plot Number:</td>
                                                                    <td>{a.plot_number || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Block Number:</td>
                                                                    <td>{a.block_number || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Location Details */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Location Details</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Ward:</td>
                                                                    <td>{a.ward_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Council:</td>
                                                                    <td>{a.council_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">District:</td>
                                                                    <td>{a.district_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Region:</td>
                                                                    <td>{a.region_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Latitude:</td>
                                                                    <td>{a.latitude || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Longitude:</td>
                                                                    <td>{a.longitude || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Institution & Building Use */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Institution & Use</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Institution Name:</td>
                                                                    <td>{a.institution_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Institution Category:</td>
                                                                    <td>{a.institution_category || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Building Use:</td>
                                                                    <td>{a.building_use_name || a.building_use?.name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Number of Buildings:</td>
                                                                    <td>{a.number_of_buildings || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Water:</td>
                                                                    <td>
                                                                        <Badge color={a.has_water ? "success" : "secondary"}>
                                                                            {a.has_water ? "Yes" : "No"}
                                                                        </Badge>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Electricity:</td>
                                                                    <td>
                                                                        <Badge color={a.has_electricity ? "success" : "secondary"}>
                                                                            {a.has_electricity ? "Yes" : "No"}
                                                                        </Badge>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Valuation Details */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Valuation Details</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Client Name:</td>
                                                                    <td>{a.client_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Project Name:</td>
                                                                    <td>{a.project_name || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Site Work Photos */}
                                                    <div className="col-12 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Site Work Photos</h6>
                                                        {loadingAllPhotos ? (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Spinner size="sm" color="primary" />
                                                                <span className="text-muted">Loading photos...</span>
                                                            </div>
                                                        ) : siteWorkPhotos[siteWorkId] && siteWorkPhotos[siteWorkId].length > 0 ? (
                                                            <div>
                                                                <div className="mb-2">
                                                                    <strong>Total Photos: {siteWorkPhotos[siteWorkId].length}</strong>
                                                                </div>
                                                                <div className="row g-3 mt-1">
                                                                    {siteWorkPhotos[siteWorkId].map((photo, idx) => (
                                                                        <div key={photo.id || idx} className="col-md-4">
                                                                            <a
                                                                                href={photo.photo_url || photo.photoUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                <img
                                                                                    src={photo.photo_url || photo.photoUrl}
                                                                                    alt={photo.photo_type || photo.photoType || `Sitework photo ${idx + 1}`}
                                                                                    className="img-fluid rounded border"
                                                                                    style={{ width: "100%", height: "180px", objectFit: "cover" }}
                                                                                />
                                                                            </a>
                                                                            <div className="small text-muted mt-2">
                                                                                {photo.photo_type || photo.photoType || "Photo"} {idx + 1}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted">No photos available for this site work.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Collapse>
                                    </td>
                        </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </Table>
        </>
    );
};

export default SiteWorksTab;
